import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — MACHINE actors (the Animation-Lite pack's heart).
 *
 * EngineActor: a real animated cutaway piston engine — piston position computed
 * from crank angle (slider-crank kinematics), connecting rod, rotating crank,
 * spark flash at TDC, intake/exhaust as VALVES (4-stroke) or WALL PORTS +
 * pumping crankcase (2-stroke), gas particles flowing in/out, and leader-line
 * labels on the parts. Pure function of `t` — deterministic, exportable.
 *
 * The Director drives it via items, e.g. ["2-stroke","transfer"] or
 * ["4-stroke","intake"] — mode + the stage/part to spotlight.
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

export interface EngineConfig {
  mode: "2-stroke" | "4-stroke";
  /** Stage/part to spotlight (dim the rest). */
  focus: "intake" | "compression" | "power" | "exhaust" | "transfer" | "none";
}

/** Parse Director items / beat text into an engine configuration. */
export function parseEngineConfig(text: string): EngineConfig {
  const s = text.toLowerCase();
  const mode: EngineConfig["mode"] = /\b(2|two)[\s-]?stroke/.test(s) ? "2-stroke" : "4-stroke";
  // Stage VERBS outrank port mentions: "closes the transfer port, compressing
  // the mixture" is a compression beat, not a transfer beat.
  const focus: EngineConfig["focus"] =
    /compress/.test(s) ? "compression"
    : /power stroke|combust|spark|ignit|explos|fires/.test(s) ? "power"
    : /transfer/.test(s) ? "transfer"
    : /intake|suck|fresh charge|carb/.test(s) ? "intake"
    : /exhaust|burnt|scaveng/.test(s) ? "exhaust"
    : "none";
  return { mode, focus };
}

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

export const EngineActor: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const cfg = parseEngineConfig(items.join(" "));
  const P = theme.palette;
  const metal = theme.dark ? "#8f99a8" : "#5a6472";
  const metalDim = theme.dark ? "#4a525e" : "#9aa4b2";
  const fresh = P.primary;              // intake charge
  const burnt = theme.dark ? "#9aa0a8" : "#7a828c"; // exhaust gas
  const dim = (on: boolean) => (cfg.focus === "none" || on ? 1 : 0.35);

  // internal unit so the whole engine fits the box
  const u = Math.min(box.h / 42, box.w / 34);
  const cx = box.cx;
  const top = box.y + (box.h - 46 * u) / 2;

  // geometry
  const boreW = 14 * u;                  // cylinder inner width
  const wall = 2 * u;
  const cylTop = top + 6 * u;
  const cylBot = cylTop + 20 * u;
  const pistonH = 5 * u;
  const R = 4.5 * u;                     // crank radius
  const L = 11 * u;                      // rod length
  const crankY = cylBot + 9 * u;

  // kinematics — one visual rev ≈ 2.2s (teaching speed)
  const theta = (t * (TAU / 2.2)) % (cfg.mode === "4-stroke" ? 2 * TAU : TAU);
  const thRev = theta % TAU;
  // piston pin y (TDC at thRev=0)
  const pinY = crankY - (R * Math.cos(thRev) + Math.sqrt(Math.max(0, L * L - Math.pow(R * Math.sin(thRev), 2))));
  const pistonTop = pinY - pistonH * 0.5;
  const crankPin = { x: cx + R * Math.sin(thRev), y: crankY - R * Math.cos(thRev) };

  // 4-stroke phases across 720°
  const phase = theta / TAU; // 0..2 (revolutions)
  const st4 =
    phase < 0.5 ? "intake" : phase < 1 ? "compression" : phase < 1.5 ? "power" : "exhaust";
  const intakeOpen = cfg.mode === "4-stroke" && st4 === "intake";
  const exhaustOpen4 = cfg.mode === "4-stroke" && st4 === "exhaust";
  const sparkNow =
    cfg.mode === "4-stroke"
      ? phase >= 1 && phase < 1.06
      : thRev < 0.12 || thRev > TAU - 0.06;

  // 2-stroke ports open when the piston crown uncovers them
  const exPortY = cylBot - 6 * u;
  const trPortY = cylBot - 4 * u;
  const exOpen2 = cfg.mode === "2-stroke" && pistonTop > exPortY;
  const trOpen2 = cfg.mode === "2-stroke" && pistonTop > trPortY;

  const label = (x: number, y: number, tx: number, ty: number, text: string, on: boolean) => (
    <g opacity={dim(on)}>
      <line x1={x} y1={y} x2={tx} y2={ty} stroke={P.muted} strokeWidth={1.2} strokeOpacity={0.8} />
      <circle cx={x} cy={y} r={1.1 * u * 0.35} fill={on && cfg.focus !== "none" ? P.primary : P.muted} />
      <text x={tx} y={ty - 0.6 * u} textAnchor={tx < cx ? "end" : "start"}
        fontFamily={theme.body.family} fontWeight={700} fontSize={2.1 * u}
        fill={on && cfg.focus !== "none" ? P.primary : P.ink} opacity={0.9}>{text}</text>
    </g>
  );

  // gas particles along a path, driven by prog 0..1 staggered
  const particles = (x1: number, y1: number, x2: number, y2: number, color: string, active: boolean, seedOff = 0) =>
    active
      ? [0, 1, 2, 3].map((i) => {
          const f = ((t * 0.9 + i / 4 + seedOff) % 1);
          return (
            <circle key={`${color}${i}`} cx={x1 + (x2 - x1) * f} cy={y1 + (y2 - y1) * f}
              r={0.8 * u * (0.7 + 0.5 * Math.sin(f * Math.PI))} fill={color}
              opacity={0.35 + 0.6 * Math.sin(f * Math.PI)} />
          );
        })
      : null;

  return (
    <g>
      {/* ── cylinder walls + head ── */}
      <rect x={cx - boreW / 2 - wall} y={cylTop - 2.5 * u} width={wall} height={cylBot - cylTop + 2.5 * u} fill={metal} opacity={dim(true)} />
      <rect x={cx + boreW / 2} y={cylTop - 2.5 * u} width={wall} height={cylBot - cylTop + 2.5 * u} fill={metal} opacity={dim(true)} />
      <rect x={cx - boreW / 2 - wall} y={cylTop - 2.5 * u} width={boreW + wall * 2} height={1.6 * u} fill={metal} />
      {/* combustion glow above piston during power */}
      {(cfg.mode === "4-stroke" ? st4 === "power" : thRev < Math.PI * 0.5) && (
        <rect x={cx - boreW / 2} y={cylTop} width={boreW} height={Math.max(0, pistonTop - cylTop)}
          fill={P.primary} opacity={0.16 * dim(cfg.focus === "power")} />
      )}

      {/* ── spark plug + flash ── */}
      <rect x={cx - 1 * u} y={cylTop - 4.4 * u} width={2 * u} height={3 * u} fill={metalDim} opacity={dim(cfg.focus === "power")} />
      {sparkNow && (
        <g opacity={dim(cfg.focus === "power")}>
          {[0, 1, 2, 3, 4].map((i) => {
            const a = (i / 5) * TAU;
            return <line key={i} x1={cx} y1={cylTop + 0.4 * u} x2={cx + Math.cos(a) * 2.6 * u} y2={cylTop + 0.4 * u + Math.sin(a) * 2.6 * u}
              stroke="#ffd75e" strokeWidth={0.5 * u} strokeLinecap="round" />;
          })}
          <circle cx={cx} cy={cylTop + 0.4 * u} r={1.2 * u} fill="#ffd75e" />
        </g>
      )}

      {/* ── mode-specific breathing ── */}
      {cfg.mode === "4-stroke" ? (
        <g>
          {/* intake valve (left) & exhaust valve (right) */}
          {(() => {
            const lift = 1.4 * u;
            const ivY = cylTop - 0.2 * u + (intakeOpen ? lift : 0);
            const evY = cylTop - 0.2 * u + (exhaustOpen4 ? lift : 0);
            return (
              <g>
                <g opacity={dim(cfg.focus === "intake")}>
                  <line x1={cx - boreW * 0.28} y1={ivY - 3.4 * u} x2={cx - boreW * 0.28} y2={ivY} stroke={metal} strokeWidth={0.9 * u} />
                  <rect x={cx - boreW * 0.28 - 1.8 * u} y={ivY} width={3.6 * u} height={0.8 * u} rx={0.3 * u} fill={intakeOpen ? P.primary : metal} />
                </g>
                <g opacity={dim(cfg.focus === "exhaust")}>
                  <line x1={cx + boreW * 0.28} y1={evY - 3.4 * u} x2={cx + boreW * 0.28} y2={evY} stroke={metal} strokeWidth={0.9 * u} />
                  <rect x={cx + boreW * 0.28 - 1.8 * u} y={evY} width={3.6 * u} height={0.8 * u} rx={0.3 * u} fill={exhaustOpen4 ? burnt : metal} />
                </g>
                {/* ducts + gas flows */}
                {particles(cx - boreW * 0.9 - 4 * u, cylTop - 3 * u, cx - boreW * 0.28, cylTop + 1.2 * u, fresh, intakeOpen)}
                {particles(cx + boreW * 0.28, cylTop + 1.2 * u, cx + boreW * 0.9 + 4 * u, cylTop - 3 * u, burnt, exhaustOpen4)}
                {label(cx - boreW * 0.28, cylTop - 2 * u, cx - boreW - 6 * u, cylTop - 3 * u, "Intake valve", cfg.focus === "intake")}
                {label(cx + boreW * 0.28, cylTop - 2 * u, cx + boreW + 6 * u, cylTop - 3 * u, "Exhaust valve", cfg.focus === "exhaust")}
              </g>
            );
          })()}
        </g>
      ) : (
        <g>
          {/* 2-stroke: exhaust port (right wall) + transfer port (left wall) + crankcase */}
          <rect x={cx + boreW / 2} y={exPortY} width={wall} height={2.2 * u} fill={exOpen2 ? burnt : metalDim} opacity={dim(cfg.focus === "exhaust")} />
          <rect x={cx - boreW / 2 - wall} y={trPortY} width={wall} height={2.2 * u} fill={trOpen2 ? fresh : metalDim} opacity={dim(cfg.focus === "transfer")} />
          {/* transfer channel from crankcase up the left side */}
          <path d={`M ${cx - boreW / 2 - wall - 1.6 * u} ${crankY - 2 * u} L ${cx - boreW / 2 - wall - 1.6 * u} ${trPortY + 1 * u} L ${cx - boreW / 2 - wall} ${trPortY + 1 * u}`}
            fill="none" stroke={metalDim} strokeWidth={1.1 * u} opacity={0.7 * dim(cfg.focus === "transfer")} />
          {particles(cx - boreW / 2 - wall - 1.6 * u, crankY - 2 * u, cx - boreW / 2 + 1 * u, trPortY + 1 * u, fresh, trOpen2)}
          {particles(cx + boreW / 2, exPortY + 1 * u, cx + boreW / 2 + 6 * u, exPortY - 2 * u, burnt, exOpen2, 0.3)}
          {/* crankcase enclosure + charge */}
          <circle cx={cx} cy={crankY} r={R + 3.4 * u} fill="none" stroke={metal} strokeWidth={1.1 * u} opacity={dim(cfg.focus === "intake" || cfg.focus === "transfer")} />
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={cx + Math.sin(t * 1.7 + i * 2.1) * R * 0.8} cy={crankY + Math.cos(t * 1.3 + i * 1.7) * R * 0.6}
              r={0.8 * u} fill={fresh} opacity={0.5 * dim(cfg.focus === "intake" || cfg.focus === "transfer")} />
          ))}
          {label(cx + boreW / 2 + wall, exPortY + u, cx + boreW + 6 * u, exPortY - 2 * u, "Exhaust port", cfg.focus === "exhaust")}
          {label(cx - boreW / 2 - wall, trPortY + u, cx - boreW - 6 * u, trPortY - 2 * u, "Transfer port", cfg.focus === "transfer")}
          {label(cx - R - 3 * u, crankY + 2 * u, cx - boreW - 6 * u, crankY + 5 * u, "Crankcase", cfg.focus === "intake")}
        </g>
      )}

      {/* ── piston + rings + pin ── */}
      <g opacity={dim(cfg.focus === "compression" || cfg.focus === "power" || cfg.focus === "none")}>
        <rect x={cx - boreW / 2} y={pistonTop} width={boreW} height={pistonH} rx={0.6 * u} fill={theme.dark ? "#c3cad4" : "#7c8694"} />
        <line x1={cx - boreW / 2} y1={pistonTop + 1 * u} x2={cx + boreW / 2} y2={pistonTop + 1 * u} stroke={metalDim} strokeWidth={0.45 * u} />
        <line x1={cx - boreW / 2} y1={pistonTop + 1.9 * u} x2={cx + boreW / 2} y2={pistonTop + 1.9 * u} stroke={metalDim} strokeWidth={0.45 * u} />
        <circle cx={cx} cy={pinY} r={0.9 * u} fill={metalDim} />
      </g>

      {/* ── connecting rod + crank ── */}
      <line x1={cx} y1={pinY} x2={crankPin.x} y2={crankPin.y} stroke={metal} strokeWidth={1.6 * u} strokeLinecap="round" />
      <circle cx={cx} cy={crankY} r={R + 1.2 * u} fill="none" stroke={metalDim} strokeWidth={0.5 * u} strokeDasharray="2 4" opacity={0.6} />
      <circle cx={cx} cy={crankY} r={2 * u} fill={metal} />
      {/* counterweight opposite the pin */}
      <circle cx={cx - R * Math.sin(thRev) * 0.7} cy={crankY + R * Math.cos(thRev) * 0.7} r={2.4 * u} fill={metalDim} />
      <circle cx={crankPin.x} cy={crankPin.y} r={1.2 * u} fill={P.primary} />
      {/* rotation arrow */}
      <path d={`M ${cx + R + 2.6 * u} ${crankY} a ${R + 2.6 * u} ${R + 2.6 * u} 0 0 1 ${-(R + 2.6 * u)} ${R + 2.6 * u}`}
        fill="none" stroke={P.muted} strokeWidth={0.5 * u} markerEnd="" opacity={0.6} />

      {label(cx + boreW / 2, pistonTop + pistonH / 2, cx + boreW + 6 * u, pistonTop + pistonH, "Piston", cfg.focus === "compression" || cfg.focus === "power")}
      {label(crankPin.x, crankPin.y, cx + boreW + 6 * u, crankY + 3 * u, "Crankshaft", false)}

      {/* stage caption */}
      <text x={cx} y={crankY + R + 7.5 * u} textAnchor="middle"
        fontFamily={theme.body.family} fontWeight={700} fontSize={2.4 * u}
        fill={P.ink} opacity={0.85}>
        {cfg.mode === "4-stroke" ? `${cfg.mode} · ${st4}` : `${cfg.mode}${exOpen2 ? " · exhaust+transfer open" : trOpen2 ? " · transfer open" : " · compressing"}`}
      </text>
    </g>
  );
};
